import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ComboboxField } from '@clay-run/glaze/combobox-field';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { useApi } from '../../_shared/hooks';
import { fetchJson } from '../../_shared/fetch-json';
import {
  campaignMembersQueryKey,
  campaignMembersSyncQueryKey,
  filterCampaignMembers,
  CampaignMembersResponseSchema,
  SyncMembersResponseSchema,
  mapCampaignMembers,
  type CampaignMember,
} from '../campaign-members';

/**
 * The "Existing campaign member" search — extracted out of `ScanReview.tsx`
 * (where it originated) so it can also mount on Home (the "search/pick a
 * campaign member" entry point for creating a pre-filled draft scan). Owns
 * its own local `memberQuery` state and both member queries; the caller only
 * decides WHAT a pick means (`onLink`) and WHICH of the two branches renders
 * (`linkedMember`).
 *
 * Copy is verbatim from the original `ScanReview.tsx` block — see that
 * component's file header for the prototype-copy-fidelity provenance.
 */

interface CampaignMemberSearchProps {
  /** `null` disables the combobox entirely and gates both queries off. */
  campaignId: string | null;
  /** Controls which of the two UI branches renders — non-null shows the
   *  linked-member summary + "Clear" button; `null` shows the combobox. */
  linkedMember: CampaignMember | null;
  onLink: (member: CampaignMember) => void;
  onUnlink: () => void;
}

export default function CampaignMemberSearch({ campaignId, linkedMember, onLink, onUnlink }: CampaignMemberSearchProps) {
  const { apiFetch } = useApi();
  const [memberQuery, setMemberQuery] = useState('');

  // The members GET below reads only the launcher's local mirror table,
  // which nothing populates for a campaign never opened in the launcher UI
  // (live-QA round-3 bug 2). Fire the launcher's synchronous member sync
  // ONCE per campaign per session before the first read. `useQuery` (not
  // `useMutation` + an effect): react-query dedupes by key across every
  // mounted consumer of this component in a batch (Home's picker AND
  // several open `ScanReview` forms can all fire the same campaign's sync at
  // once), `staleTime: Infinity` stops any refire this session, and there is
  // no ref/effect for StrictMode to strand.
  const membersSyncQuery = useQuery({
    queryKey: campaignMembersSyncQueryKey(campaignId),
    enabled: Boolean(campaignId),
    staleTime: Infinity,
    retry: false,
    // A failed sync must not fire the app-wide error toast — the mirror
    // simply stays whatever it already held, and the members read below
    // proceeds against that (possibly stale/empty) data rather than
    // blocking the rep from filling the form by hand.
    meta: { suppressErrorToast: true },
    queryFn: async () => {
      return fetchJson(apiFetch, SyncMembersResponseSchema, `/api/tools/launcher/campaigns/${campaignId}/sync-members`, {
        method: 'POST',
      });
    },
  });

  // Optional existing-campaign-member typeahead — tracks whichever campaign
  // is CURRENTLY active (the caller decides which one that is).
  const membersQuery = useQuery({
    queryKey: campaignMembersQueryKey(campaignId),
    // `isFetched` covers success OR error — a failed sync must not block
    // searching whatever the mirror already holds.
    enabled: Boolean(campaignId) && membersSyncQuery.isFetched,
    queryFn: async () => {
      const rows = await fetchJson(
        apiFetch,
        CampaignMembersResponseSchema,
        `/api/tools/launcher/campaigns/${campaignId}/members`,
      );
      return mapCampaignMembers(rows);
    },
  });

  // "Either leg of the two-step member read is still in flight" — the sync
  // POST, or the members GET it gates. Read by BOTH the loading line and the
  // empty-state line below, which is what keeps them mutually exclusive.
  const isLoadingMembers = membersSyncQuery.isFetching || membersQuery.isFetching;

  const members = membersQuery.data ?? [];
  const trimmedMemberQuery = memberQuery.trim();
  const filteredMembers = filterCampaignMembers(members, memberQuery);

  return linkedMember ? (
    <div className="flex flex-col gap-2">
      <Text size="sm" weight="semibold">
        Linked campaign member
      </Text>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <Text size="sm" weight="semibold" className="truncate">
            {linkedMember.name}
          </Text>
          <Text size="xs" variant="tertiary" className="truncate">
            {[linkedMember.title, linkedMember.accountName].filter(Boolean).join(' · ')}
          </Text>
        </div>
        <Button variant="ghost" size="sm" onClick={onUnlink}>
          Clear
        </Button>
      </div>
      <Text size="xs" variant="tertiary">
        Fields below were prefilled from this member's record. Clearing the link leaves whatever is in the form.
      </Text>
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      <Text size="sm" weight="semibold">
        Existing campaign member <span className="font-normal text-content-tertiary">· optional</span>
      </Text>
      <ComboboxField
        aria-label="Search existing campaign members"
        placeholder="Search existing campaign members…"
        items={filteredMembers}
        itemToStringLabel={(m: CampaignMember) => m.name}
        value={null}
        onValueChange={(m) => m && onLink(m)}
        inputValue={memberQuery}
        onInputValueChange={setMemberQuery}
        by="id"
        disabled={!campaignId}
        // The real SFDC sync round trip (bug 2) can take a moment before
        // the mirror has anything to search — a spinner beats a
        // silently-empty result.
        loading={membersSyncQuery.isLoading || membersQuery.isLoading}
      >
        {filteredMembers.map((m) => {
          const detail = [m.title, m.accountName].filter(Boolean).join(' · ');
          return (
          <ComboboxOption key={m.id} value={m}>
            <span className="min-w-0 flex-1 truncate">
              {m.name}
              {/* Guarded, unlike the previous unconditional `{m.name} — {join}`:
                  a member with neither a title nor an account rendered a
                  dangling "Dana Rivera — ". */}
              {detail && <span className="text-content-tertiary"> — {detail}</span>}
            </span>
            {/* Status is rendered as its own trailing column, not folded into
                the ' · ' detail join above (live-QA round 4, bug 1). Spencer's
                ask is browsing BY status, which means the rep is scanning a
                vertical column of values — a value buried mid-sentence
                between title and company can't be scanned that way, and is
                also indistinguishable from a company literally named
                "Responded". */}
            {m.status && <span className="ml-2 shrink-0 text-xs text-content-tertiary">{m.status}</span>}
          </ComboboxOption>
          );
        })}
      </ComboboxField>
      {/* Mutually exclusive on purpose: "no matches" is only true once there
          is nothing left in flight that could still produce one — otherwise
          both lines render stacked while the SFDC sync round trip is still
          running, telling the rep the search failed before it finished. */}
      {isLoadingMembers ? (
        <Text size="xs" variant="tertiary">
          Loading campaign members…
        </Text>
      ) : trimmedMemberQuery ? (
        filteredMembers.length === 0 && (
          <Text size="xs" variant="tertiary">
            No campaign members match “{memberQuery}” — keep filling the form below.
          </Text>
        )
      ) : (
        // Blank query + nothing loaded is now its own state worth naming.
        // Before round 4 the blank query ALWAYS produced an empty list, so
        // "the dropdown is empty" carried no information; now that a blank
        // query browses the full roster, an empty dropdown means the roster
        // itself is empty — and saying so is what stops this from reading as
        // "the arrow is broken" all over again.
        members.length === 0 && (
          <Text size="xs" variant="tertiary">
            No campaign members synced for this event yet — keep filling the form below.
          </Text>
        )
      )}
      <Text size="xs" variant="tertiary">
        Search or open the dropdown to browse everyone already on this campaign — picking someone prefills the form.
      </Text>
    </div>
  );
}
